import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { RefreshCw } from "lucide-react";

import NavBarPortal from "../../Layout/NavBar/NavBarPortal";
import { clx } from "../../../utils/Helpers";

import "./DashboardRefresh.scss";

const MINUTE_MS = 60 * 1000;

/**
 * Elapsed minutes since `timestamp`, re-computed every minute so the label
 * ages on its own while the tab stays open without a refresh.
 */
const useElapsedMinutes = (timestamp) => {
  const [minutes, setMinutes] = useState(0);

  useEffect(() => {
    const tick = () =>
      setMinutes(Math.floor((Date.now() - timestamp) / MINUTE_MS));

    tick();
    const intervalId = setInterval(tick, MINUTE_MS);
    return () => clearInterval(intervalId);
  }, [timestamp]);

  return minutes;
};

/**
 * Dashboard "Refresh data" action — renders into the navbar action slot beside
 * the notifications bell. Figma: "Button" (12931:32105) — white pill with a
 * primary label and the age of the figures on screen in foreground/subtle.
 * `labels.minutesAgo` / `labels.hoursAgo` carry a `{value}` slot.
 */
const DashboardRefresh = ({ labels, updatedAt, loading, onRefresh }) => {
  const minutes = useElapsedMinutes(updatedAt);
  const hours = Math.floor(minutes / 60);

  let updatedLabel = labels.justNow;
  if (hours >= 1) {
    updatedLabel = labels.hoursAgo.replace("{value}", hours);
  } else if (minutes >= 1) {
    updatedLabel = labels.minutesAgo.replace("{value}", minutes);
  }

  return (
    <NavBarPortal slot="action">
      <button
        type="button"
        className={clx("dashboard-refresh", loading && "dashboard-refresh--loading")}
        onClick={onRefresh}
        disabled={loading}
        aria-busy={loading ? "true" : undefined}
      >
        <RefreshCw
          className="dashboard-refresh__icon"
          size={15}
          strokeWidth={1.5}
          aria-hidden="true"
        />
        <span className="dashboard-refresh__label">{labels.refresh}</span>
        <span className="dashboard-refresh__updated">
          <span className="dashboard-refresh__separator" aria-hidden="true">
            ·
          </span>
          {loading ? labels.refreshing : updatedLabel}
        </span>
      </button>
    </NavBarPortal>
  );
};

DashboardRefresh.propTypes = {
  labels: PropTypes.shape({
    refresh: PropTypes.string.isRequired,
    refreshing: PropTypes.string.isRequired,
    justNow: PropTypes.string.isRequired,
    minutesAgo: PropTypes.string.isRequired,
    hoursAgo: PropTypes.string.isRequired,
  }).isRequired,
  /** Epoch ms of the last successful load. */
  updatedAt: PropTypes.number.isRequired,
  loading: PropTypes.bool,
  onRefresh: PropTypes.func.isRequired,
};

DashboardRefresh.defaultProps = {
  loading: false,
};

export default DashboardRefresh;
